import { EnvironmentProviders, Provider } from '@angular/core';
import { FirebaseApp, getApps, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { Auth, connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import {
	connectFirestoreEmulator,
	Firestore,
	FirestoreError,
	getFirestore,
	initializeFirestore,
	persistentLocalCache,
	persistentMultipleTabManager,
	provideFirestore,
} from '@angular/fire/firestore';
import { connectFunctionsEmulator, Functions, getFunctions, provideFunctions } from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { connectStorageEmulator, FirebaseStorage, getStorage } from '@angular/fire/storage';
import { EMULATOR_HOST, SCBTEAM_EMULATOR_PORTS, SCBWEB_EMULATOR_PORTS } from '@scbt-env/emulators';
import { environment, SCBTEAM_FIREBASE_CONFIG, SCBWEB_FIREBASE_CONFIG } from '@scbt-env/environment';
import { storageGet } from '@scbt-lib/index';
import { FUNCTION_REGIONS } from '@scbweb-lib/index';
import { SCBWEB_APP, SCBWEB_AUTH, SCBWEB_FIRESTORE, SCBWEB_FN_VERSION, SCBWEB_FUNCTIONS, SCBWEB_STORAGE } from './firebase.tokens';

/** Name the scb-website app is registered under. The only place this string should appear. */
export const SCBWEB_APP_NAME = 'scbWeb';

const emu = environment.emulators;

/**
 * Kill switch for the persistent cache, checked at bootstrap on every build including production.
 *
 * `localStorage.setItem('DISABLE_FS_PERSISTENCE', 'true')` + reload turns the Firestore cache off for
 * both projects, without a rebuild or a deploy. It exists so that "is this bug caused by the offline
 * cache?" can be answered in one reload on the machine that is actually showing the bug — which is
 * otherwise a rebuild-and-redeploy round trip. Same `storageGet` idiom as SHOW_DEV_CONSOLE in main.ts.
 *
 * Turning it on does not evict what IndexedDB already holds; it only stops the SDK reading from and
 * writing to it. To start genuinely cold, delete the `firestore/...` databases in
 * DevTools -> Application -> IndexedDB as well.
 */
const DISABLE_PERSISTENCE_KEY = 'DISABLE_FS_PERSISTENCE';
const persistenceDisabledByFlag = (): boolean => storageGet(DISABLE_PERSISTENCE_KEY) === 'true';

/**
 * Turn on Firestore's **persistent, multi-tab-shared** IndexedDB cache for `app`.
 *
 * Without this every browser tab is an independent Firestore client: its own WebChannel
 * connection, its own full download of every collection. With it, all tabs of the same
 * FirebaseApp share one IndexedDB database, only the lease-holding tab keeps a network
 * connection, and a reload resumes each listener from a token instead of re-reading the
 * collection.
 *
 * **Must run before anything calls `getFirestore(app)`** — `initializeFirestore` throws
 * `failed-precondition` once the instance exists. There are three places that can produce a
 * FirebaseApp here (the two `provideFirebaseApp` factories and the SCBWEB_APP fallback below),
 * so this is called from all three and made idempotent: a second call for the same app swallows
 * the `failed-precondition` it would raise and leaves the first call's settings in place.
 *
 * **Skipped when that project's Firestore emulator is in use.** IndexedDB outlives an emulator
 * restart and the emulator's dataset does not, so a warm cache would keep serving documents the
 * backend no longer has, indistinguishable from real ones.
 *
 * No `'indexedDB' in window` probe and no try/catch around the cache decision: `initializeFirestore`
 * and `persistentLocalCache` only record settings, synchronously — they never touch IndexedDB. The
 * real IDB work happens lazily on the first read, inside the SDK's `ensureOfflineComponents`, which
 * already catches quota/abort/private-browsing failures, logs
 * `"Error using user provided cache. Falling back to memory cache: ..."` and installs the memory
 * cache. A hand-rolled probe would be strictly worse — it returns true in Firefox private browsing,
 * and cannot test LocalStorage, which `persistentMultipleTabManager` also needs for its
 * `firestore_clients_*` lease keys.
 *
 * NOTE the import source. Every Firestore symbol in this file comes from `@angular/fire/firestore`,
 * never from `firebase/firestore`. npm nests a second, older copy of the SDK under @angular/fire
 * (11.10.0 / @firebase/firestore 4.8.0) and *that* is the copy that ships; the app's own
 * `firebase` 12.17.1 resolves to a different `@firebase/app` registry. Importing these two symbols
 * from `firebase/firestore` type-checks and builds a cache object belonging to the wrong SDK
 * instance, which only fails at runtime. This looks like pointless indirection — it is not.
 */
function withPersistentCache(app: FirebaseApp, emulated: boolean): FirebaseApp {
	if (emulated || persistenceDisabledByFlag()) {
		if (!environment.production) {
			const why = emulated ? 'emulator in use' : `localStorage ${DISABLE_PERSISTENCE_KEY}=true`;
			console.info(`Firestore persistence disabled for app "${app.name}" (${why})`);
		}
		return app;
	}
	try {
		initializeFirestore(app, {
			// Do NOT add `cacheSizeBytes` next to `localCache` — initializeFirestore throws
			// `invalid-argument` when both are set. The default 40 MB LRU threshold applies per database.
			localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
		});
	} catch (e) {
		// Already initialized for this app (another of the three entry points got there first).
		if ((e as FirestoreError)?.code !== 'failed-precondition') throw e;
	}
	return app;
}

/**
 * scb-website deploys prod and test copies of every callable into the same project, each into its
 * own region (FUNCTION_REGIONS: prod = europe-west1, test = europe-west8). A dev build therefore
 * talks to the `...Test` callables in europe-west8 — or to the emulator, which serves both.
 */
const scbWebFnVersion: 'Prod' | 'Test' = environment.production ? 'Prod' : 'Test';
const scbWebFnRegion = environment.production ? FUNCTION_REGIONS.prod : FUNCTION_REGIONS.test;

/**
 * Providers for the **default** Firebase app (project scb-team). Its services keep being injected
 * the plain AngularFire way — `inject(Firestore)`, `inject(Auth)`, `inject(Functions)`.
 */
export const scbTeamFirebaseProviders = [
	provideFirebaseApp(() => withPersistentCache(initializeApp(SCBTEAM_FIREBASE_CONFIG), emu.team.firestore)),
	provideAuth(() => {
		const auth = getAuth();
		if (emu.team.auth) {
			connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${SCBTEAM_EMULATOR_PORTS.auth}`, { disableWarnings: true });
		}
		return auth;
	}),
	provideFirestore(() => {
		const firestore = getFirestore();
		if (emu.team.firestore) {
			connectFirestoreEmulator(firestore, EMULATOR_HOST, SCBTEAM_EMULATOR_PORTS.firestore);
		}
		return firestore;
	}),
	provideFunctions(() => {
		const functions = getFunctions();
		if (emu.team.functions) {
			connectFunctionsEmulator(functions, EMULATOR_HOST, SCBTEAM_EMULATOR_PORTS.functions);
		}
		return functions;
	}),
	provideMessaging(() => getMessaging()),
];

/**
 * Providers for the **secondary** Firebase app (project scb-website), exposed through the tokens in
 * [firebase.tokens.ts](./firebase.tokens.ts). Every emulator decision for that project is made here
 * and nowhere else.
 *
 * Must be spread *after* {@link scbTeamFirebaseProviders} so scb-team stays the default app.
 */
export const scbWebFirebaseProviders: (Provider | EnvironmentProviders)[] = [
	provideFirebaseApp(() => withPersistentCache(initializeApp(SCBWEB_FIREBASE_CONFIG, SCBWEB_APP_NAME), emu.web.firestore)),
	{
		provide: SCBWEB_APP,
		// Falls back to initializing the app itself so the token cannot depend on provider ordering:
		// injecting it before AngularFire's own factory has run would otherwise throw.
		useFactory: (): FirebaseApp =>
			withPersistentCache(
				getApps().find((a) => a.name === SCBWEB_APP_NAME) ?? initializeApp(SCBWEB_FIREBASE_CONFIG, SCBWEB_APP_NAME),
				emu.web.firestore
			),
	},
	{
		provide: SCBWEB_AUTH,
		useFactory: (app: FirebaseApp): Auth => {
			const auth = getAuth(app);
			if (emu.web.auth) {
				connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${SCBWEB_EMULATOR_PORTS.auth}`, { disableWarnings: true });
			}
			return auth;
		},
		deps: [SCBWEB_APP],
	},
	{
		provide: SCBWEB_FIRESTORE,
		useFactory: (app: FirebaseApp): Firestore => {
			const firestore = getFirestore(app);
			if (emu.web.firestore) {
				connectFirestoreEmulator(firestore, EMULATOR_HOST, SCBWEB_EMULATOR_PORTS.firestore);
			}
			return firestore;
		},
		deps: [SCBWEB_APP],
	},
	{
		provide: SCBWEB_FUNCTIONS,
		useFactory: (app: FirebaseApp): Functions => {
			const functions = getFunctions(app, scbWebFnRegion);
			if (emu.web.functions) {
				connectFunctionsEmulator(functions, EMULATOR_HOST, SCBWEB_EMULATOR_PORTS.functions);
			}
			return functions;
		},
		deps: [SCBWEB_APP],
	},
	{
		provide: SCBWEB_STORAGE,
		useFactory: (app: FirebaseApp): FirebaseStorage => {
			const storage = getStorage(app);
			if (emu.web.storage) {
				connectStorageEmulator(storage, EMULATOR_HOST, SCBWEB_EMULATOR_PORTS.storage);
			}
			return storage;
		},
		deps: [SCBWEB_APP],
	},
	{ provide: SCBWEB_FN_VERSION, useValue: scbWebFnVersion },
];

/**
 * One line at startup saying which backends this session is really talking to — the thing you
 * actually want when a query returns unexpected data and you can't remember how you started the app.
 */
export function logFirebaseTargets(): void {
	if (environment.production) return;

	const target = (on: boolean, port: number) => (on ? `emulator:${port}` : 'CLOUD');
	console.info(
		`%cFirebase targets%c\n` +
			`  scb-team   auth=${target(emu.team.auth, SCBTEAM_EMULATOR_PORTS.auth)}` +
			` firestore=${target(emu.team.firestore, SCBTEAM_EMULATOR_PORTS.firestore)}` +
			` functions=${target(emu.team.functions, SCBTEAM_EMULATOR_PORTS.functions)}\n` +
			`  scb-website auth=${target(emu.web.auth, SCBWEB_EMULATOR_PORTS.auth)}` +
			` firestore=${target(emu.web.firestore, SCBWEB_EMULATOR_PORTS.firestore)}` +
			` functions=${target(emu.web.functions, SCBWEB_EMULATOR_PORTS.functions)}` +
			` storage=${target(emu.web.storage, SCBWEB_EMULATOR_PORTS.storage)}` +
			` callables=*${scbWebFnVersion} @ ${scbWebFnRegion}`,
		'font-weight:bold',
		'font-weight:normal'
	);
}
